// frontend/src/theme/create-theme.js
import { createTheme as createMuiTheme } from '@mui/material/styles';
import { GLASS } from '@caro/shared/theme';
import { applyPreset, presets } from './theme-color-presets';
import { BRAND_THEMES, isBrandTheme } from './brand-themes';
import { deepmerge, mergeComponents } from './merge-components';
import { DEFAULT_PORTAL_THEME } from './defaults';

// The theme an unknown `themeColor` resolves to — the portal default, explicitly, through the
// registry, never through applyPreset's silent `presets[0]` fallback (F-1, IR-5, AC-14).
const FALLBACK_BRAND_THEME = isBrandTheme(DEFAULT_PORTAL_THEME) ? DEFAULT_PORTAL_THEME : 'plum';

// Radius scale for themes that do not bring their own (theme.caro.radius).
const DEFAULT_RADIUS = { xs: 4, sm: 6, md: 8, lg: 12, xl: 16, device: 44, pill: 999 };

// Status pairs for the colour presets, derived from the resolved MUI palette.
const deriveStatus = (palette) => {
  const pair = (c) => ({ bg: c.light, fg: c.dark, border: c.main });
  return {
    available: pair(palette.success),
    rollout: pair(palette.warning),
    urgent: pair(palette.error),
    brand: pair(palette.primary),
    neutral: { bg: palette.grey[200], fg: palette.grey[800], border: palette.grey[300] },
  };
};

/**
 * Resolve `themeColor` to a brand plugin and/or a colour preset:
 *   brand theme (plum, gold, editorial) → its plugin, no preset
 *   known colour preset                 → no plugin, applyPreset
 *   anything else                       → the plum plugin
 */
const resolveThemeColor = (themeColor) => {
  if (isBrandTheme(themeColor)) return { brandName: themeColor, presetName: null };
  if (presets.some((p) => p.name === themeColor)) return { brandName: null, presetName: themeColor };
  return { brandName: FALLBACK_BRAND_THEME, presetName: null };
};

// Create theme with settings including accessibility options
export const createTheme = (language = 'en', settings = {}) => {
  const {
    direction = 'ltr',
    themeMode = 'light',
    themeColor = DEFAULT_PORTAL_THEME,
    typography = 'Nunito, sans-serif',
    textSize = 'small',
    contrastMode = false,
    reducedMotion = false,
    density = 'comfortable',
  } = settings;

  const compact = density === 'compact';

  // Base font size based on text size setting
  const baseFontSize = {
    small: 14,
    medium: 16,
    large: 18,
    xlarge: 20,
  }[textSize] || 14;

  // Select the brand plugin (registry) or colour preset for this themeColor.
  const { brandName, presetName } = resolveThemeColor(themeColor);
  const brandTheme = brandName ? BRAND_THEMES[brandName](themeMode, { baseFontSize }) : {};

  // ── DE DRIE GLASWAARDEN VOOR DEZE MODUS ──────────────────────────────────
  //
  // Eén keer uitgerekend en daarna hergebruikt, zodat de balk, het zijmenu en de dialogen
  // gegarandeerd hetzelfde glas dragen. Ze uit elkaar laten lopen is precies hoe er eerder
  // drie verschillende blur-waarden in deze codebase belandden.
  const isDark = themeMode === 'dark';
  const glassBg = isDark ? GLASS.backgroundStrongDark : GLASS.backgroundStrong;
  const glassBgLight = isDark ? GLASS.backgroundDark : GLASS.background;
  const glassBorder = isDark ? GLASS.borderDark : GLASS.border;
  const glassShadow = isDark ? GLASS.shadowDark : GLASS.shadow;

  // Dark mode paints neutral greys over background/text — unless the plugin brings its own dark
  // palette (Editorial, F-3). High contrast keeps its own values under every theme (AC-6).
  const brandPalette = brandTheme.palette || {};
  const applyDarkGreys = themeMode === 'dark' && (!brandTheme.ownsDarkPalette || contrastMode);

  // Create base theme
  let theme = createMuiTheme({
    direction,
    palette: {
      mode: themeMode,
      ...brandPalette,
      ...(applyDarkGreys && {
        background: {
          ...(brandPalette.background || {}),
          default: contrastMode ? '#141a21' : '#121212',
          paper: contrastMode ? '#1c252e' : '#1e1e1e',
        },
        text: {
          ...(brandPalette.text || {}),
          primary: contrastMode ? '#FFFFFF' : '#c7cdd1',
          secondary: contrastMode ? '#c7cdd1' : '#a0a0a0',
        },
      }),
    },
    // Base typography first; a plugin's typography deep-merges over it. Under Editorial the
    // plugin sets the font families itself, so the stored `typography` choice is ignored (LD-5)
    // while sizes keep coming from textSize.
    typography: deepmerge({
      fontFamily: typography,
      fontSize: baseFontSize,
      h1: { fontSize: baseFontSize * 2.5, fontWeight: 700 },
      h2: { fontSize: baseFontSize * 2, fontWeight: 700 },
      h3: { fontSize: baseFontSize * 1.75, fontWeight: 600 },
      h4: { fontSize: baseFontSize * 1.5, fontWeight: 600 },
      h5: { fontSize: baseFontSize * 1.25, fontWeight: 500 },
      h6: { fontSize: baseFontSize * 1.1, fontWeight: 500 },
      body1: { fontSize: baseFontSize },
      body2: { fontSize: baseFontSize * 0.875 },
      button: { fontWeight: 600, textTransform: 'none' },
    }, brandTheme.typography),
    shape: brandTheme.shape || {
      borderRadius: 8,
    },
    customShadows: brandTheme.customShadows || {},
    gradients: brandTheme.gradients || {},
    // Base overrides first; the plugin's overrides deep-merge into them per component and per
    // slot (merge-components.js, IR-1) — never a shallow `{ ...base, ...plugin }`.
    components: mergeComponents({
      // ── GLAS OP NAVIGATIE EN DIALOGEN ─────────────────────────────────────
      //
      // Hier en niet in de dertig plekken die een dialoog openen: dit is de enige plaats
      // waar het één keer geregeld is, en waar niemand het per ongeluk anders doet.
      //
      // De portal kent zijn eigen licht/donker via `palette.mode`, dus hier worden de
      // CONSTANTEN gebruikt en niet de CSS-variabelen: die laatste kantelen mee met
      // `[data-theme]` van de bewonersapp en zouden hier de verkeerde kant op kunnen.
      //
      // Dialogen krijgen de DICHTE variant. Een formulier of een tabel op te veel
      // doorschijn leest slecht, en leesbaarheid weegt zwaarder dan het effect.
      MuiButton: {
        styleOverrides: {
          root: {
            // Increase padding for better touch targets
            padding: textSize === 'large' || textSize === 'xlarge' 
              ? '8px 20px' 
              : undefined,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          contained: {
            boxShadow: brandTheme.customShadows?.button || 'none',
            '&:hover': {
              boxShadow: brandTheme.customShadows?.button || 'none',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: brandTheme.customShadows?.card || 'none',
            borderRadius: 12,
            position: 'relative',
            zIndex: 0,
            transition: 'transform 180ms ease, box-shadow 180ms ease',
          },
        },
        variants: [
          {
            // Opt-in interactive lift — cards that use `<Card variant="interactive">`
            // rise on hover. Non-interactive cards keep their default look.
            props: { variant: 'interactive' },
            style: ({ theme: t }) => ({
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: t.shadows[4],
              },
            }),
          },
        ],
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: glassBg,
            backdropFilter: GLASS.filter,
            // Zonder voorvoegsel geen glas maar een doorzichtig vlak op oudere WebKit.
            WebkitBackdropFilter: GLASS.filter,
            border: `1px solid ${glassBorder}`,
            boxShadow: brandTheme.customShadows?.dialog || glassShadow,
            borderRadius: 12,
          },
        },
      },
      // De balk bovenaan zweeft over de inhoud, dus die mag de LICHTE variant: je ziet
      // eronder doorschemeren wat er staat, en dat helpt bij het scrollen.
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: glassBgLight,
            backdropFilter: GLASS.filter,
            WebkitBackdropFilter: GLASS.filter,
            borderBottom: `1px solid ${glassBorder}`,
            boxShadow: 'none',
            backgroundImage: 'none',
            // De tekst kan niet meer op de merkkleur leunen nu de balk doorschijnend is.
            color: isDark ? '#F7FAFC' : 'rgba(0, 0, 0, 0.87)',
          },
          // ⚠ `root` ALLEEN is niet genoeg. Een AppBar staat standaard op `color="primary"`,
          // en die klasse zet de achtergrond NA `root` -- dan blijft de balk gewoon de
          // merkkleur en gebeurt er zichtbaar niets. Vandaar dat de kleurklassen hier
          // expliciet meedoen.
          colorPrimary: {
            backgroundColor: glassBgLight,
            color: isDark ? '#F7FAFC' : 'rgba(0, 0, 0, 0.87)',
          },
          colorDefault: {
            backgroundColor: glassBgLight,
          },
        },
      },
      // Het zijmenu draagt navigatie -- tekst die scherp moet blijven. Dichte variant.
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: glassBg,
            backdropFilter: GLASS.filter,
            WebkitBackdropFilter: GLASS.filter,
            borderRight: `1px solid ${glassBorder}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: baseFontSize * 0.75,
            backgroundColor: themeMode === 'light' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)',
            color: themeMode === 'light' ? '#fff' : '#000',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: baseFontSize,
          },
        },
      },
      // Touch-target comfort — raise IconButton minimum hit area on
      // smaller viewports so taps don't require pixel-perfect aim.
      // 40x40 on mobile (finger-friendly), auto on desktop to keep
      // existing compact layouts untouched.
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minWidth: 40,
              minHeight: 40,
            },
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              minWidth: 36,
              minHeight: 36,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minHeight: 44,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minHeight: 44,
            },
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 64,
          },
        },
      },
      // Row density — flip every Table into small spacing when the
      // user picks compact mode. Individual tables can still pass an
      // explicit `size` to override.
      MuiTable: {
        defaultProps: {
          size: compact ? 'small' : 'medium',
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: compact ? 4 : undefined,
            paddingBottom: compact ? 4 : undefined,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: compact ? 2 : undefined,
            paddingBottom: compact ? 2 : undefined,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            // Enhance link underlines for better visibility
            textDecorationThickness: contrastMode ? '2px' : undefined,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            // Prevent iOS from zooming form inputs when focused — keeps
            // editors stable across orientations.
            WebkitTextSizeAdjust: '100%',
            // Thin scrollbars on Firefox. These used to sit in a SECOND `html` key further
            // down, which silently replaced this block so WebkitTextSizeAdjust was never
            // applied (F-4, spec 20260930-caro-editorial-theme). One block now.
            scrollbarColor: `${themeMode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'} transparent`,
            scrollbarWidth: 'thin',
          },
          body: {
            // Apply reduced motion if enabled
            ...(reducedMotion && {
              '& *': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            }),
            // Respect iOS home indicator / notch when the body is the
            // scroll container. Individual surfaces (drawers, bars)
            // still honour safe-area via their own CSS.
            overscrollBehavior: 'none',
          },
          // Visible focus ring using the brand primary color — shows
          // only on keyboard navigation (`:focus-visible`), so mouse
          // users don't see outlines after clicking.
          '*:focus-visible': {
            outline: (brandTheme.palette?.primary?.main
              ? `2px solid ${brandTheme.palette.primary.main}`
              : '2px solid #9A3F69'),
            outlineOffset: '2px',
            borderRadius: '4px',
          },
          // Thin, brand-friendly scrollbars. Honours the user's dark/light
          // mode and keeps the classic scrollbar feel on Firefox via
          // `scrollbar-*` properties.
          '*::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
            borderRadius: '8px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: brandTheme.palette?.primary?.main || '#9A3F69',
          },
          // Notistack mobile lift — keep toasts clear of the fixed
          // bottom nav (56px) + iOS home indicator.
          '.caro-snackbar-root.SnackbarContainer-bottom': {
            bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 8px) !important',
          },
          '@media (min-width: 900px)': {
            '.caro-snackbar-root.SnackbarContainer-bottom': {
              bottom: '16px !important',
            },
          },
        },
      },
    }, brandTheme.components),
  });

  // Colour presets only swap the primary colour; brand themes never go through here.
  if (presetName) {
    theme = applyPreset(theme, presetName);
  }

  // ── theme.caro: WHAT CONSUMERS READ INSTEAD OF BRANCHING ON themeColor ─────────
  //
  // Every theme carries the same shape, so Layout.js, chips and pages never need
  // `if (themeColor === 'editorial')`. Plum, gold and the presets describe today's look here
  // (glass sidebar, palette-derived status pairs); none of it is applied by the theme itself.
  const isSolidSidebar = Boolean(brandTheme.sidebar?.solid);
  const sidebar = isSolidSidebar
    ? brandTheme.sidebar
    : {
        solid: false,
        bg: glassBg,
        fg: theme.palette.text.primary,
        fgMuted: theme.palette.text.secondary,
        itemHoverBg: theme.palette.action.hover,
        itemSelectedBg: theme.palette.action.selected,
        accent: theme.palette.primary.main,
        divider: glassBorder,
      };
  const status = brandTheme.status || deriveStatus(theme.palette);

  // Chip styling for a status pair (owner decision D-2): only themes that define status chips
  // (Editorial) return styles; everywhere else it is a no-op, so plum/gold/presets keep rendering
  // exactly what the chip's MUI `color` gives today.
  const statusChips = Boolean(brandTheme.statusChips);
  const statusSx = (kind) => {
    if (!statusChips) return {};
    const pair = status[kind] || status.neutral;
    return {
      backgroundColor: pair.bg,
      color: pair.fg,
      borderColor: pair.border,
      '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: pair.fg },
    };
  };

  theme.caro = {
    sidebar,
    status,
    statusChips,
    statusSx,
    radius: brandTheme.radius || DEFAULT_RADIUS,
    fontFamilyDisplay: theme.typography.fontFamilyDisplay || theme.typography.fontFamily,
    fontFamilyMono: theme.typography.fontFamilyMono || 'ui-monospace, SFMono-Regular, Menlo, monospace',
    legacy: brandTheme.legacy || {},
  };

  return theme;
};

export default createTheme;